import React, { useState, useMemo } from "react";
import { View, TouchableOpacity } from "react-native";
import { StyleSheet } from "@/utils/appStyleSheet";
import { AppText } from "@/components/AppText";
import { PurchasedProductWithRefundProcess } from "@/types/purchasedProduct";
import { PhoneRefundFlowModal } from "../hotelReservation/PhoneRefundFlowModal";
import { ProductPrice } from "@/types/productPrice";
import { FontFamilies } from "@/constants/Typography";
import Icons from "@/assets/icons";
import { TRACKING_EVENTS } from "@/constants/trackingEvents";
import { useTrackImpression, useTrackClick } from "@/hooks/tracking";

type Props = {
  product: PurchasedProductWithRefundProcess;
};

const PurchasedProductCard: React.FC<Props> = ({ product }) => {
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [selectedPrice, setSelectedPrice] = useState<ProductPrice | undefined>(
    undefined
  );
  const hasRefundStatus = !!product.currentStatus;

  const savingsAmount = useMemo(() => {
    if (product.lowerPriceProduct?.extractedPrice) {
      return (product.usdPrice ?? 0) - product.lowerPriceProduct.extractedPrice;
    }
    return product.usdPrice ?? 0;
  }, [product.usdPrice, product.lowerPriceProduct?.extractedPrice]);

  // Track when card is shown
  useTrackImpression(TRACKING_EVENTS.PRODUCT_REFUND_SHOWN, {
    productName: product.productName,
    retailer: product.retailer,
    amount: savingsAmount,
  });

  // Track when "Request refund" button is clicked
  const handleStartRefund = useTrackClick(
    TRACKING_EVENTS.PRODUCT_REFUND_CLICKED,
    {
      productName: product.productName,
      retailer: product.retailer,
      amount: savingsAmount,
    }
  )(() => {
    setShowRefundModal(true);
  });

  const handleCloseModal = () => {
    setShowRefundModal(false);
    setSelectedPrice(undefined);
  };

  return (
    <View style={styles.card}>
      {/* Top section with amount and title */}
      <View style={styles.topSection}>
        <View style={styles.productImageContainer}>
          <View style={styles.productLogo}>
            <AppText style={styles.productLogoText}>
              {product.retailer.charAt(0).toUpperCase()}
            </AppText>
          </View>
        </View>
        <View style={styles.leftContent}>
          <AppText style={styles.titleText}>
            Price match on {product.retailer} order
          </AppText>
          <AppText style={styles.amountText}>
            ${savingsAmount.toFixed(2)}
          </AppText>
        </View>
      </View>

      {/* Divider */}
      <View style={styles.divider} />

      {/* Bottom buttons */}
      <View style={styles.buttonSection}>
        <View style={styles.statusContent}>
          <Icons.NoProofPaper width={16} height={16} />
          <AppText style={styles.statusText}>
            {hasRefundStatus
              ? `Refund ${product.currentStatus}`
              : `Found $${savingsAmount.toFixed(2)} better price on ${
                  product.lowerPriceProduct?.source || "Target.com"
                }`}
          </AppText>
        </View>
        <TouchableOpacity
          style={styles.requestButton}
          onPress={handleStartRefund}
        >
          <AppText style={styles.requestButtonText}>Request refund</AppText>
        </TouchableOpacity>
      </View>

      <PhoneRefundFlowModal
        visible={showRefundModal}
        item={product}
        onClose={handleCloseModal}
        selectedPrice={selectedPrice}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  card: {
    backgroundColor: "#FFFFFF",
    borderRadius: 16,
    marginHorizontal: 16,
    marginVertical: 8,
    borderWidth: 1,
    borderColor: "#EBEBEB",
    overflow: "hidden",
  },
  topSection: {
    flexDirection: "row",
    alignItems: "center",
    padding: 12,
  },
  leftContent: {
    flex: 1,
    marginLeft: 16,
  },
  amountText: {
    fontFamily: FontFamilies.InterTight.SemiBold,
    fontSize: 20,
    color: "#161D14",
  },
  titleText: {
    fontFamily: FontFamilies.Inter.Regular,
    fontSize: 15,
    color: "#161D14",
  },
  productImageContainer: {
    width: 80,
    height: 80,
    borderRadius: 16,
    backgroundColor: "#F7F8FA",
    justifyContent: "center",
    alignItems: "center",
  },
  productLogo: {
    width: 64,
    height: 64,
    borderRadius: 32,
    backgroundColor: "#E5E7EB",
    justifyContent: "center",
    alignItems: "center",
  },
  productLogoText: {
    fontSize: 24,
    fontWeight: "600",
    color: "#FFFFFF",
  },
  divider: {
    height: 1,
    backgroundColor: "#F3F3F3",
  },
  statusContent: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    flex: 1,
  },
  statusText: {
    fontFamily: FontFamilies.Inter.Regular,
    fontSize: 13,
    fontWeight: "400",
    color: "#161D14",
    lineHeight: 13 * 1.5385, // 20px from Figma
    letterSpacing: -0.02 * 13, // -2% of font size
  },
  buttonSection: {
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    gap: 10,
    padding: 8, // Reduced from 16 to 8px to match Figma
    paddingHorizontal: 16,
  },
  requestButton: {
    backgroundColor: "#0F9129",
    borderWidth: 1,
    borderColor: "rgba(255, 255, 255, 0.12)",
    borderRadius: 100,
    paddingHorizontal: 20,
    height: 39,
    justifyContent: "center",
    alignItems: "center",
  },
  requestButtonText: {
    fontFamily: FontFamilies.InterTight.SemiBold,
    fontSize: 15,
    fontWeight: "600",
    color: "#FFFFFF",
  },
});

export default PurchasedProductCard;
